import React, { useState } from "react";
import {Box, Form, FormField, Text, TextInput} from "grommet";
import { useRouter } from 'next/router'
import NarrowHeading from "../../components/NarrowHeading";
import StatusMessage from "../../components/StatusMessage";
import NextAnchor from "../../components/NextAnchor";
import {signIn, getSession} from "next-auth/react";
import LoginPageLayout from "../../components/layouts/LoginPageLayout";
import {useTheme} from "next-themes";
import {identifyUser} from "../../utils/segment";
import { clearAdminUser } from "../../utils/router";
import Confirm from "../../components/Confirm";
import Button from "../../components/Button";
import PasskeyLoginButton from "../../components/PasskeyLoginButton";
import GoogleSignInButton from "../../components/GoogleSignInButton";
import MicrosoftSignInButton from "../../components/MicrosoftSignInButton";

const Signin = ({ alert, welcomeMessage}) => {
    const router = useRouter();
    const [step, setStep] = useState('email'); // 'email' or 'password'
    const [email, setEmail] = useState('');
    const [showConfirmation, setShowConfirmation] = useState();
    const [emailCopy, setEmailCopy] = useState();
    const [passwordCopy, setPasswordCopy] = useState();
    const [errorMessage, setErrorMessage] = useState(alert);
    const { setTheme } = useTheme();

    // Extract query params only after router is ready to avoid hydration mismatch
    const source = router.isReady ? router.query.source : undefined;
    const redirectPath = router.isReady ? router.query.redirectPath : undefined;

    const applyTheme = (session) => {
        switch (session?.user?.theme) {
        case "auto":
            setTheme('system');
            break;
        case "light":
            setTheme('light');
            break;
        case "dark":
            setTheme('dark');
            break;
        default:
            setTheme('light');
            break;
        }
    };

    const onEmailSubmit = (event) => {
        setEmail(event.value.email);
        setErrorMessage(null);
        setStep('password');
    };

    const onPasswordSubmit = async (event) => {
        const postData = { email, password: event.value.password, callbackUrl: "/", redirect: false };
        setEmailCopy(email);
        setPasswordCopy(event?.value?.password);
        signIn("credentials", postData)
            .then(async (data) => {
                if (data.error) {
                    if (data.error.includes("302")) {
                        const session = await getSession();
                        applyTheme(session);
                        if (session) identifyUser(session.user);
                        return setShowConfirmation(true);
                    } else if (data.error.includes('401') && (data.error || '').toLowerCase().includes('rejected')) {
                        router.push("/account-unavailable");
                        return;
                    } else if (data.error.includes('401') && (data.error || '').toLowerCase().includes('suspended')) {
                        router.push("/account-under-review");
                        return;
                    } else if (data.error.includes('503')) {
                        setErrorMessage({
                            status: 503,
                            type: "error",
                            message: "The application is temporarily unavailable. Please try again later."
                        });
                        return;
                    } else if (data.error.includes('google_oauth_user')) {
                        setErrorMessage({
                            status: 400,
                            type: "info",
                            message: "This account uses Google Sign-In. Please sign in with Google."
                        });
                        setStep('email'); // Go back to email step to show Google button
                        return;
                    } else {
                        setErrorMessage({
                            status: 400,
                            type: "error",
                            message: "Incorrect email and/or password."
                        });
                        return;
                    }
                }
                if (data.status === 200) {
                    const session = await getSession();
                    applyTheme(session);
                    if (session) identifyUser(session.user);
                    clearAdminUser();
                    const redirectTo = redirectPath || new URL(data?.url).pathname || "/";
                    router.replace(redirectTo);
                } else if (data.status === 503) {
                    setErrorMessage({
                        status: 503,
                        type: "error",
                        message: "The application is temporarily unavailable. Please try again later."
                    });
                } else {
                    setErrorMessage({
                        status: 400,
                        type: "error",
                        message: "Incorrect email and/or password."
                    });
                }
            })
            .catch((error) => {
                clearAdminUser();
                setErrorMessage({
                    status: 500,
                    type: "error",
                    message: "Something went wrong. Please try again and contact us at help@streamlinedpayments.com if the problem continues."
                });
            });
    };

    const handleChangeEmail = () => {
        setErrorMessage(null);
        setStep('email');
    };

    if (showConfirmation) {
        return <Confirm email={emailCopy} password={passwordCopy} />;
    }

    return (
        <LoginPageLayout title="Sign In" pad={{ top: "medium" }}>
            <NarrowHeading
                heading={welcomeMessage || 'Welcome back!'}
                paragraph="Continue to Streamlined."
            />
            <Box pad={{ top: "xsmall", bottom: "small" }}>
                {errorMessage && <StatusMessage apiError={errorMessage} />}
                {source === "shopify" && !errorMessage && (
                    <StatusMessage
                        apiError={{ message: "Your store already has an account. Please sign in." }}
                    />
                )}

                {step === 'email' ? (
                    <>
                        <Form
                            onSubmit={onEmailSubmit}
                            messages={{
                                required: "This field is required",
                            }}
                        >
                            <Box margin={{ vertical: "xsmall" }}>
                                <FormField
                                    required
                                    validate={{
                                        regexp: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                                        message: "Please enter a valid email address",
                                    }}
                                    component={TextInput}
                                    placeholder="Work email"
                                    name="email"
                                    autoComplete="email"
                                    data-testid="signin-form-email"
                                />
                                <Box margin={{ top: "medium" }}>
                                    <Button
                                        event={{section: 'sign_in_form', element: 'continue'}}
                                        type="submit"
                                        primary
                                        label="Continue"
                                        data-testid="signin-form-continue" />
                                </Box>
                            </Box>
                        </Form>
                        <Box margin={{ vertical: "small" }} border={{ side: "bottom", color: "border-weak" }} />
                        <Box margin={{ top: "xsmall" }}>
                            <PasskeyLoginButton
                                onSuccess={async () => {
                                    const session = await getSession();
                                    applyTheme(session);
                                    if (session) identifyUser(session.user);
                                    clearAdminUser();
                                    router.replace(redirectPath || '/');
                                }}
                                onError={(error) => setErrorMessage({
                                    status: 400,
                                    type: "error",
                                    message: error || "Passkey authentication failed."
                                })}
                                callbackUrl={redirectPath || '/'}
                            />
                        </Box>
                        <Box margin={{ top: "xsmall" }}>
                            <GoogleSignInButton
                                callbackUrl={redirectPath || '/'}
                            />
                        </Box>
                        <Box margin={{ top: "xsmall" }}>
                            <MicrosoftSignInButton
                                callbackUrl={redirectPath || '/'}
                            />
                        </Box>
                        {!source && (
                            <Box margin={{ top: "medium" }}>
                                <Text textAlign="start">
                                    Need to streamline your invoicing?{" "}
                                    <NextAnchor
                                        href={process.env.NODE_ENV === 'production' ? (process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview' ? '/signup' : "https://streamlinedpayments.com/sign-up") : '/signup'}
                                        primary
                                        label="Sign up for free" />
                                </Text>
                            </Box>
                        )}
                    </>
                ) : (
                    <>
                        <Box direction="row" align="center" gap="small" margin={{ bottom: "xsmall" }}>
                            <Text>{email}</Text>
                            <Button
                                label="Change"
                                size="small"
                                onClick={handleChangeEmail}
                            />
                        </Box>
                        <Form
                            onSubmit={onPasswordSubmit}
                            messages={{
                                required: "This field is required",
                            }}
                        >
                            <Box margin={{ vertical: "xsmall" }}>
                                <FormField
                                    required
                                    component={TextInput}
                                    placeholder="Password"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    data-testid="signin-form-password"
                                />
                                <Box margin={{ top: "xxsmall" }}>
                                    <NextAnchor href="/session/password-reset" primary label="Forgot password?" size="small" />
                                </Box>
                                <Box margin={{ top: "medium" }}>
                                    <Button
                                        event={{section: 'sign_in_form', element: 'submit'}}
                                        type="submit"
                                        primary
                                        label="Log in"
                                        data-testid="signin-form-submit" />
                                </Box>
                            </Box>
                        </Form>
                    </>
                )}
            </Box>
        </LoginPageLayout>
    );
}

export async function getServerSideProps ({query, resolvedUrl}) {
    // Redirect /session/signout to /session/signin
    if (resolvedUrl?.startsWith('/session/signout')) {
        return {
            redirect: {
                destination: '/session/signin',
                permanent: false,
            },
        };
    }

    if (query.hasOwnProperty('error')) {
        if (query.error === '500') {
            return {
                props: {
                    alert: {
                        status: 500,
                        type: "error",
                        message: "Something went wrong. Please try again and contact us at help@streamlinedpayments.com if the problem continues."
                    }
                }
            };
        } else if (query.error === '503') {
            return {
                props: {
                    alert: {
                        status: 503,
                        type: "error",
                        message: "The application is temporarily unavailable. Please try again later."
                    }
                }
            };
        } else if (query.error === 'no_account') {
            return {
                props: {
                    alert: {
                        status: 401,
                        type: "error",
                        message: "You must be invited to join. Please contact your administrator."
                    }
                }
            };
        } else if (query.error === 'google_oauth_user') {
            return {
                props: {
                    alert: {
                        status: 400,
                        type: "info",
                        message: "This account uses Google Sign-In. Please sign in with Google."
                    }
                }
            };
        } else if (query.error === 'email_mismatch') {
            return {
                props: {
                    alert: {
                        status: 400,
                        type: "error",
                        message: "The Google email doesn't match the invitation email. Please use the correct Google account."
                    }
                }
            };
        } else if (query.error === 'invalid_invitation') {
            return {
                props: {
                    alert: {
                        status: 400,
                        type: "error",
                        message: "Invalid or expired invitation. Please request a new invitation."
                    }
                }
            };
        } else if (query.error === 'google_auth_failed') {
            return {
                props: {
                    alert: {
                        status: 400,
                        type: "error",
                        message: "Google sign-in failed. Please try again."
                    }
                }
            };
        } else if (query.error === 'microsoft_auth_failed') {
            return {
                props: {
                    alert: {
                        status: 400,
                        type: "error",
                        message: "Microsoft sign-in failed. Please try again."
                    }
                }
            };
        } else {
            return {
                props: {
                    alert: {
                        status: 400,
                        type: "error",
                        message: "Incorrect email and/or password."
                    }
                }
            };
        }
    } else {
        const today = new Date();
        const day = today.getDay(); // 0 = Sunday, 6 = Saturday
        const time = today.getHours();
        const month = today.getMonth() + 1; // 1-12
        const dayOfMonth = today.getDate();

        // Check for special holidays first
        const getHolidayMessage = () => {
            // New Year's Day
            if (month === 1 && dayOfMonth === 1) return "Happy New Year! 🎉";
            // Valentine's Day
            if (month === 2 && dayOfMonth === 14) return "Happy Valentine's Day! 💕";
            // St. Patrick's Day
            if (month === 3 && dayOfMonth === 17) return "Happy St. Patrick's Day! 🍀";
            // April Fools
            if (month === 4 && dayOfMonth === 1) return "No fooling around today! 🃏";
            // Cinco de Mayo
            if (month === 5 && dayOfMonth === 5) return "¡Feliz Cinco de Mayo! 🎊";
            // Independence Day (US)
            if (month === 7 && dayOfMonth === 4) return "Happy 4th of July! 🇺🇸";
            // Halloween
            if (month === 10 && dayOfMonth === 31) return "Happy Halloween! 🎃";
            // Day after Halloween
            if (month === 11 && dayOfMonth === 1) return "Recover from the candy? 🍬";
            // Thanksgiving (4th Thursday of November - approximate)
            if (month === 11 && dayOfMonth >= 22 && dayOfMonth <= 28 && day === 4) return "Happy Thanksgiving! 🦃";
            // Black Friday
            if (month === 11 && dayOfMonth >= 23 && dayOfMonth <= 29 && day === 5) return "Happy Black Friday! 🛍️";
            // Christmas Eve & Christmas
            if (month === 12 && dayOfMonth === 24) return "Merry Christmas Eve! 🎄";
            if (month === 12 && dayOfMonth === 25) return "Merry Christmas! 🎄";
            // New Year's Eve
            if (month === 12 && dayOfMonth === 31) return "Happy New Year's Eve! 🥂";
            // Holiday season
            if (month === 12 && dayOfMonth >= 20) return "Happy Holidays! ✨";
            return null;
        };

        // Seasonal messages
        const getSeasonalMessage = () => {
            if (month >= 3 && month <= 5) return "Spring is in the air! 🌸";
            if (month >= 6 && month <= 8) return "Summer vibes! ☀️";
            if (month >= 9 && month <= 11) return "Fall is here! 🍂";
            return "Winter wonderland! ❄️";
        };

        // Time-based messages
        const getTimeMessage = () => {
            if (time < 5) return "Burning the midnight oil?";
            if (time < 7) return "Rise and shine! ☀️";
            if (time < 12) return "Good morning!";
            if (time < 14) return "Lunchtime hustle?";
            if (time < 18) return "Good afternoon!";
            if (time < 21) return "Good evening!";
            return "Working late?";
        };

        // Day-based messages
        const getDayMessage = () => {
            if (day === 0) return "Sunday funday?";
            if (day === 1) return "Happy Monday! 💪";
            if (day === 4) return "Happy almost Friday!";
            if (day === 5) return "Happy Friday! 🎉";
            if (day === 6) return "Weekend mode! 🎊";
            return null;
        };

        const holidayMessage = getHolidayMessage();
        if (holidayMessage) {
            return { props: { welcomeMessage: holidayMessage } };
        }

        const welcomeMessages = [
            "Welcome back!",
            "Hi there!",
            "Hello!",
            "Hello again!",
            "Good to see you!",
            "Let's get things done!",
            "Ready to streamline?",
            getTimeMessage(),
            getDayMessage(),
            getSeasonalMessage(),
            "🙌",
        ].filter((e) => e);

        const selectWelcomeMessage = () => {
            const random = (time + day + dayOfMonth) % welcomeMessages.length;
            return welcomeMessages[random];
        };

        return {
            props: {
                welcomeMessage: selectWelcomeMessage()
            }
        };
    }
}

export default Signin;

Signin.auth = false;
